import React, { useState } from "react";

import { mdiEyeOutline } from "@mdi/js";
import Icon from "@mdi/react";
import classNames from "classnames";
import { get, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import ErrorMessage from "./ErrorMessage";
import FieldContainer from "./FieldContainer";
import Label from "./Label";
import { containerClasses, inputClasses, wrapperClasses } from "./styles";

import type { JSX } from "react";

import type { InputProps } from "./types";

type PasswordInputProps = Pick<
  InputProps,
  | "icon"
  | "label"
  | "name"
  | "sublabel"
  | "isRequired"
  | "minLength"
  | "className"
  | "isCompact"
  | "isNoWrap"
  | "validate"
  | "onBlurCustom"
  | "isTooltipErrorMessage"
  | "isFullWidth"
>;

export default function PasswordInput({
  icon,
  name,
  label,
  sublabel,
  isRequired = false,
  minLength = 0,
  className,
  isCompact,
  isNoWrap,
  validate,
  onBlurCustom,
  isTooltipErrorMessage,
  isFullWidth,
}: PasswordInputProps): JSX.Element {
  const {
    register,
    getFieldState,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation("editor-errors");

  const [type, setType] = useState("password");

  const isInvalid = getFieldState(name)?.invalid;
  const error = get(errors, name);

  const { onBlur, ...rest } = register(name, {
    required: isRequired && t("requiredField"),
    minLength,
    validate,
  });

  const inputClassNames = classNames(
    "pr-8.75",
    inputClasses({
      icon,
      isInvalid,
      isReadOnly: false,
    }),
  );

  const handleClick = (): void => {
    setType((prevType) => (prevType === "password" ? "text" : "password"));
  };

  return (
    <FieldContainer
      className={className}
      isCompact={isCompact}
      isFullWidth={isFullWidth}
      isNoWrap={isNoWrap}
    >
      {label ? (
        <Label
          isCompact={isCompact}
          isFullWidth={isFullWidth}
          isRequired={isRequired}
          label={label}
          name={name}
          sublabel={sublabel}
        />
      ) : null}
      <div className={wrapperClasses({ isCompact })}>
        <div className={containerClasses()}>
          <div className="text-muted-foreground pointer-events-none absolute inset-y-0 left-0 flex items-center pl-[0.46875rem]">
            {icon}
          </div>
          <div className="relative flex flex-1 items-center">
            <input
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...rest}
              className={inputClassNames}
              data-tooltip-content={error?.message}
              data-tooltip-id={`field-${name}-tooltip`}
              data-tooltip-position-strategy="fixed"
              defaultValue=""
              type={type}
              onBlur={(e): void => {
                onBlur(e);
                onBlurCustom?.(e);
              }}
            />
            <div
              className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-[0.46875rem]"
              onClick={handleClick}
            >
              <Icon path={mdiEyeOutline} size={0.875} />
            </div>
          </div>
        </div>
        <ErrorMessage
          error={error}
          isTooltip={isTooltipErrorMessage}
          name={name}
        />
      </div>
    </FieldContainer>
  );
}
